// src/App.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Warehouse, Plane, Fuel, FileText, Settings, 
  Menu, X, LogOut, Bell, User, Building2, Calendar, MessageSquare, Car,
  Globe, ClipboardList, TrendingUp, Wrench, Package, Key, Cpu,
  Truck, Receipt, DollarSign, Activity, Shield, Server,
  MapPin, Repeat, Award, History, ChevronDown, ChevronRight,
  AlertTriangle, CheckSquare, BarChart3
} from 'lucide-react';

// Import des composants existants
import DashboardView from './components/DashboardView';
import ClientsView from './components/ClientsView';
import HangarsView from './components/HangarsView';
import AircraftView from './components/AircraftView';
import FuelView from './components/FuelView';
import InvoicesView from './components/InvoicesView';
import SettingsView from './components/SettingsView';
import ScheduleView from './components/ScheduleView';
import ChatView from './components/ChatView';
import 'leaflet/dist/leaflet.css';
import VehiclesView from './components/VehiclesView';
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import ClientRequestsView from './components/staff/ClientRequestsView';
import FuelAnalytics from './components/analytics/FuelAnalytics';
import MaintenanceAnalytics from './components/analytics/MaintenanceAnalytics';
import FinancialAnalytics from './components/analytics/FinancialAnalytics';
import SuppliersView from './components/Suppliers/SuppliersView';
import SupplierInvoicesView from './components/SupplierInvoices/SupplierInvoicesView';
import TakeoffLandingAnalytics from './components/analytics/TakeoffLandingAnalytics';
import HangarLeaseAnalytics from './components/analytics/hangar-lease/HangarLeaseAnalytics';

// Import des nouveaux composants
import InspectionsView from './components/inspections/InspectionsView';
import AirportZonesView from './components/zones/AirportZonesView';
import RecurringTasksView from './components/tasks/RecurringTasksView';
import StaffCertifications from './components/staff/StaffCertifications';
import DeletionHistoryView from './components/admin/DeletionHistoryView';

// Import Auth
import { useAuth } from './contexts/AuthContext';

// Import SysAdmin Dashboard
import SysAdminDashboard from './pages/sysadmin/SysAdminDashboard';

// ── MOBILE — import + hook ──────────────────────────────────────────
import MobileStaffDashboard from './components/staff/mobile/MobileStaffDashboard';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};
// ─────────────────────────────────────────────────────────────────────

// ── Niveaux autorisés ──────────────────────────────────────────────────────────
const FLIGHT_ANALYTICS_LEVELS = new Set([90, 100, 110]);
const SETTINGS_LEVELS         = new Set([90, 100, 110]);
const SUPER_ADMIN_LEVEL       = 110;
const SYSADMIN_LEVEL          = 105;

// Niveaux pour les fonctionnalités Airport Operations
const INSPECTIONS_LEVELS      = new Set([20, 30, 50, 75, 90, 100, 110]);
const ZONES_LEVELS            = new Set([75, 90, 100, 110]);
const RECURRING_TASKS_LEVELS  = new Set([75, 90, 100, 110]);
const CERTIFICATIONS_LEVELS   = new Set([50, 75, 90, 100, 110]);
const DELETION_AUDIT_LEVELS   = new Set([75, 90, 100, 110]);

// ─── Composant AccessDenied ────────────────────────────────────────────────────
const AccessDenied = ({ icon: Icon, label, message }) => (
  <div className="flex flex-col items-center justify-center h-64 text-center">
    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl mb-4">
      <Icon size={32} className="text-red-400 mx-auto mb-2" />
      <p className="text-red-400 font-semibold">Access Restricted</p>
      <p className="text-gray-400 text-sm mt-1">{label} — {message}</p>
    </div>
  </div>
);

// ─── Composant Sous-Menu ────────────────────────────────────────────────────────
const SubMenuItem = ({ id, label, icon: Icon, color, active, onClick }) => {
  return (
    <button
      onClick={() => onClick(id)}
      className={`w-full flex items-center gap-3 pl-10 pr-3 py-2 rounded-lg transition-all duration-200 ${
        active
          ? 'bg-blue-600/20 text-blue-400 border-l-2 border-blue-500'
          : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
      }`}
    >
      <Icon size={18} className={active ? color : ''} />
      <span className="text-sm">{label}</span>
    </button>
  );
};

// ─── Composant Menu Item avec Sous-Menu ────────────────────────────────────────
const MenuItemWithSubmenu = ({ item, activeMain, isOpen, onToggle, activeSub, onNavigate, sidebarOpen }) => {
  const Icon = item.icon;
  
  const handleSubItemClick = (subItemId) => {
    onNavigate(subItemId);
    if (isOpen) {
      onToggle();
    }
  };
  
  return (
    <div>
      <button
        onClick={onToggle}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
          activeMain ? 'bg-blue-600/20 text-blue-400 border-l-2 border-blue-500' : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
        }`}
      >
        <Icon size={20} className={activeMain ? item.color : ''} />
        {sidebarOpen && (
          <>
            <span className="text-sm flex-1 text-left">{item.label}</span>
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </>
        )}
      </button>
      
      {isOpen && sidebarOpen && (
        <div className="mt-1 space-y-1">
          {item.subItems.map((subItem) => (
            <SubMenuItem
              key={subItem.id}
              id={subItem.id}
              label={subItem.label}
              icon={subItem.icon}
              color={subItem.color}
              active={activeSub === subItem.id}
              onClick={handleSubItemClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Composant principal ───────────────────────────────────────────────────────
const App = () => {
  const { user, logout, isAuthenticated, loading, getUserLevel } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [notifications] = useState(3);
  const navigate = useNavigate();
  const location = useLocation();

  // ── MOBILE HOOK ──────────────────────────────────────────────────────────
  const isMobile = useIsMobile();

  const userLevel = getUserLevel();
  const isSuperAdmin = userLevel === SUPER_ADMIN_LEVEL;
  const isSysAdmin = userLevel >= SYSADMIN_LEVEL && !isSuperAdmin;
  const canViewFlightAnalytics = FLIGHT_ANALYTICS_LEVELS.has(userLevel);
  const canViewSettings = SETTINGS_LEVELS.has(userLevel);
  
  // Vérifications pour Airport Operations
  const canViewAirportOps = INSPECTIONS_LEVELS.has(userLevel) || 
                            ZONES_LEVELS.has(userLevel) || 
                            RECURRING_TASKS_LEVELS.has(userLevel) ||
                            CERTIFICATIONS_LEVELS.has(userLevel) ||
                            DELETION_AUDIT_LEVELS.has(userLevel);

  // Configuration du sous-menu Airport Operations
  const airportOpsSubItems = [
    { id: 'inspections', label: 'Inspections', icon: ClipboardList, color: 'text-blue-400' },
    { id: 'airportZones', label: 'Airport Zones', icon: MapPin, color: 'text-green-400' },
    { id: 'recurringTasks', label: 'Recurring Tasks', icon: Repeat, color: 'text-purple-400' },
    { id: 'staffCertifications', label: 'Certifications', icon: Award, color: 'text-yellow-400' },
    { id: 'deletionAudit', label: 'Deletion Audit', icon: History, color: 'text-red-400' }
  ].filter(item => {
    if (item.id === 'airportZones') return ZONES_LEVELS.has(userLevel);
    if (item.id === 'recurringTasks') return RECURRING_TASKS_LEVELS.has(userLevel);
    if (item.id === 'staffCertifications') return CERTIFICATIONS_LEVELS.has(userLevel);
    if (item.id === 'deletionAudit') return DELETION_AUDIT_LEVELS.has(userLevel);
    if (item.id === 'inspections') return INSPECTIONS_LEVELS.has(userLevel);
    return false;
  });

  // Configuration du sous-menu Analytics
  const analyticsSubItems = [
    { id: 'fuelAnalytics', label: 'Fuel Analytics', icon: TrendingUp, color: 'text-indigo-400' },
    { id: 'maintAnalytics', label: 'Maint Analytics', icon: Wrench, color: 'text-red-400' },
    { id: 'financialAnalytics', label: 'Financial Analytics', icon: DollarSign, color: 'text-green-400' }
  ];

  if (canViewFlightAnalytics) {
    analyticsSubItems.push({ id: 'flightAnalytics', label: 'Flight Analytics', icon: Activity, color: 'text-sky-400' });
  }
  
  analyticsSubItems.push({ id: 'hangarLeaseAnalytics', label: 'Hangar Analytics', icon: Warehouse, color: 'text-yellow-400' });

  // Menu de base (items plats)
  const flatMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, color: 'text-blue-400' },
    { id: 'schedule', label: 'Schedule', icon: Calendar, color: 'text-cyan-400' },
    { id: 'chat', label: 'Chat', icon: MessageSquare, color: 'text-pink-400' },
    { id: 'clientRequests', label: 'Client Requests', icon: ClipboardList, color: 'text-purple-400' },
    { id: 'clients', label: 'Clients', icon: Users, color: 'text-green-400' },
    { id: 'suppliers', label: 'Suppliers', icon: Truck, color: 'text-emerald-400' },
    { id: 'hangars', label: 'Hangars & Leases', icon: Warehouse, color: 'text-yellow-400' },
    { id: 'aircraft', label: 'Aircraft', icon: Plane, color: 'text-purple-400' },
    { id: 'fuel', label: 'Fuel Management', icon: Fuel, color: 'text-orange-400' },
    { id: 'invoices', label: 'Invoices', icon: FileText, color: 'text-red-400' },
    { id: 'supplierInvoices', label: 'Supplier Invoices', icon: Receipt, color: 'text-indigo-400' },
    { id: 'vehicles', label: 'Vehicles', icon: Car, color: 'text-sky-400' }
  ];

  if (canViewSettings) {
    flatMenuItems.push({ id: 'settings', label: 'Settings', icon: Settings, color: 'text-gray-400' });
  }

  // Synchroniser activeTab avec l'URL
  useEffect(() => {
    const path = location.pathname;
    
    const match = path.match(/^\/app\/(.+)$/);
    if (match) {
      const tab = match[1];
      const allMenuIds = [...flatMenuItems.map(m => m.id), ...analyticsSubItems.map(a => a.id), ...airportOpsSubItems.map(s => s.id)];
      if (allMenuIds.includes(tab)) {
        setActiveTab(tab);
      }
    } else if (path === '/app' || path === '/app/') {
      setActiveTab('dashboard');
    }
  }, [location, flatMenuItems, analyticsSubItems, airportOpsSubItems]);

  const handleNavigate = (tabId) => {
    setActiveTab(tabId);
    if (tabId === 'dashboard') {
      navigate('/app');
    } else {
      navigate(`/app/${tabId}`);
    }
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  const toggleSubmenu = (menuId) => {
    setOpenSubmenu(openSubmenu === menuId ? null : menuId);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // MOBILE SWITCH — Smartphone (< 768px) → interface mobile native
  // ─────────────────────────────────────────────────────────────────────────────
  if (isMobile) return <MobileStaffDashboard />;

  // ─────────────────────────────────────────────────────────────────────────────
  // DESKTOP — code original intact ci-dessous
  // ─────────────────────────────────────────────────────────────────────────────
  const renderContent = () => {
    // Vérifications d'accès
    if (activeTab === 'flightAnalytics' && !canViewFlightAnalytics) {
      return <AccessDenied icon={Activity} label="Flight Analytics" message="Access restricted" />;
    }
    if (activeTab === 'settings' && !canViewSettings) {
      return <AccessDenied icon={Settings} label="Settings" message="Access restricted" />;
    }
    if (activeTab === 'platformAdmin' && !isSuperAdmin) {
      return <AccessDenied icon={Shield} label="Platform Admin" message="Access restricted" />;
    }
    if (activeTab === 'sysadmin' && !isSuperAdmin && !isSysAdmin) {
      return <AccessDenied icon={Server} label="Infrastructure" message="Access restricted" />;
    }
    
    // Vérifications Airport Operations
    if (activeTab === 'airportZones' && !ZONES_LEVELS.has(userLevel)) {
      return <AccessDenied icon={MapPin} label="Airport Zones" message="Access restricted" />;
    }
    if (activeTab === 'recurringTasks' && !RECURRING_TASKS_LEVELS.has(userLevel)) {
      return <AccessDenied icon={Repeat} label="Recurring Tasks" message="Access restricted" />;
    }
    if (activeTab === 'staffCertifications' && !CERTIFICATIONS_LEVELS.has(userLevel)) {
      return <AccessDenied icon={Award} label="Staff Certifications" message="Access restricted" />;
    }
    if (activeTab === 'deletionAudit' && !DELETION_AUDIT_LEVELS.has(userLevel)) {
      return <AccessDenied icon={History} label="Deletion Audit" message="Access restricted" />;
    }

    // Switch principal
    switch (activeTab) {
      case 'dashboard': return <DashboardView />;
      case 'schedule': return <ScheduleView />;
      case 'chat': return <ChatView />;
      case 'clientRequests': return <ClientRequestsView />;
      case 'clients': return <ClientsView />;
      case 'suppliers': return <SuppliersView />;
      case 'hangars': return <HangarsView />;
      case 'aircraft': return <AircraftView />;
      case 'fuel': return <FuelView />;
      case 'invoices': return <InvoicesView />;
      case 'supplierInvoices': return <SupplierInvoicesView />;
      case 'vehicles': return <VehiclesView />;
      case 'settings': return <SettingsView />;
      case 'fuelAnalytics': return <FuelAnalytics />;
      case 'maintAnalytics': return <MaintenanceAnalytics />;
      case 'financialAnalytics': return <FinancialAnalytics />;
      case 'flightAnalytics': return <TakeoffLandingAnalytics />;
      case 'hangarLeaseAnalytics': return <HangarLeaseAnalytics />;
      case 'inspections': return <InspectionsView />;
      case 'airportZones': return <AirportZonesView />;
      case 'recurringTasks': return <RecurringTasksView />;
      case 'staffCertifications': return <StaffCertifications />;
      case 'deletionAudit': return <DeletionHistoryView />;
      case 'platformAdmin': return <SuperAdminDashboard />;
      case 'sysadmin': return <SysAdminDashboard />;
      default: return <DashboardView />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const displayName = user ? `${user.first_name} ${user.last_name}` : 'Admin';
  const displayRole = user ? user.role?.charAt(0).toUpperCase() + user.role?.slice(1) : 'Administrator';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full bg-gray-900/95 backdrop-blur-sm border-r border-gray-700 transition-all duration-300 z-20 ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          {sidebarOpen && (
            <div>
              <h1 className="text-xl font-bold text-white">aeroTrick</h1>
              <p className="text-xs text-gray-500">FBO Management</p>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white">
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100%-140px)]">
          {/* Items plats (Dashboard, Schedule, etc.) */}
          {flatMenuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavigate(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                activeTab === item.id
                  ? 'bg-blue-600/20 text-blue-400 border-l-2 border-blue-500'
                  : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
              }`}
            >
              <item.icon size={20} className={activeTab === item.id ? item.color : ''} />
              {sidebarOpen && <span className="text-sm">{item.label}</span>}
            </button>
          ))}

          {/* Séparateur avant Analytics */}
          {sidebarOpen && analyticsSubItems.length > 0 && (
            <div className="pt-2 pb-1">
              <div className="border-t border-gray-700/60" />
            </div>
          )}
          
          {/* Sous-menu Analytics */}
          {analyticsSubItems.length > 0 && (
            <MenuItemWithSubmenu
              item={{ 
                id: 'analytics', 
                label: 'Analytics', 
                icon: BarChart3, 
                color: 'text-purple-400',
                subItems: analyticsSubItems
              }}
              activeMain={analyticsSubItems.some(s => s.id === activeTab)}
              isOpen={openSubmenu === 'analytics'}
              onToggle={() => toggleSubmenu('analytics')}
              activeSub={activeTab}
              onNavigate={handleNavigate}
              sidebarOpen={sidebarOpen}
            />
          )}

          {/* Séparateur avant Airport Operations */}
          {canViewAirportOps && sidebarOpen && (
            <div className="pt-2 pb-1">
              <div className="border-t border-gray-700/60" />
            </div>
          )}
          
          {/* Sous-menu Airport Operations */}
          {canViewAirportOps && (
            <MenuItemWithSubmenu
              item={{ 
                id: 'airportOps', 
                label: 'Airport Operations', 
                icon: Building2, 
                color: 'text-cyan-400',
                subItems: airportOpsSubItems
              }}
              activeMain={airportOpsSubItems.some(s => s.id === activeTab)}
              isOpen={openSubmenu === 'airportOps'}
              onToggle={() => toggleSubmenu('airportOps')}
              activeSub={activeTab}
              onNavigate={handleNavigate}
              sidebarOpen={sidebarOpen}
            />
          )}

          {/* SECTION PLATFORM ADMIN (SuperAdmin uniquement) */}
          {isSuperAdmin && sidebarOpen && (
            <div className="pt-2 pb-1">
              <div className="border-t border-gray-700/60" />
              <p className="text-xs text-gray-600 uppercase tracking-wider px-3 pt-2">Platform</p>
            </div>
          )}
          
          {isSuperAdmin && (
            <button
              onClick={() => handleNavigate('platformAdmin')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                activeTab === 'platformAdmin'
                  ? 'bg-blue-600/20 text-blue-400 border-l-2 border-blue-500'
                  : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
              }`}
            >
              <Shield size={20} className={activeTab === 'platformAdmin' ? 'text-red-400' : ''} />
              {sidebarOpen && <span className="text-sm">Platform Admin</span>}
            </button>
          )}

          {/* SECTION INFRASTRUCTURE (SysAdmin ou SuperAdmin) */}
          {(isSuperAdmin || isSysAdmin) && sidebarOpen && (
            <div className="pt-2 pb-1">
              <div className="border-t border-gray-700/60" />
              <p className="text-xs text-gray-600 uppercase tracking-wider px-3 pt-2">Infrastructure</p>
            </div>
          )}
          
          {(isSuperAdmin || isSysAdmin) && (
            <button
              onClick={() => handleNavigate('sysadmin')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                activeTab === 'sysadmin'
                  ? 'bg-blue-600/20 text-blue-400 border-l-2 border-blue-500'
                  : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
              }`}
            >
              <Server size={20} className={activeTab === 'sysadmin' ? 'text-purple-400' : ''} />
              {sidebarOpen && <span className="text-sm">Infrastructure</span>}
            </button>
          )}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
              <User size={16} className="text-white" />
            </div>
            {sidebarOpen && (
              <div className="flex-1">
                <p className="text-sm text-white">{displayName}</p>
                <p className="text-xs text-gray-500">{displayRole}</p>
              </div>
            )}
            <button onClick={logout} className="text-gray-400 hover:text-white">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        <div className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-700 px-6 py-3 flex justify-between items-center">
          <h2 className="text-white font-semibold">
            {activeTab === 'analytics' ? 'Analytics' :
             activeTab === 'airportOps' ? 'Airport Operations' :
             flatMenuItems.find(m => m.id === activeTab)?.label ||
             analyticsSubItems.find(a => a.id === activeTab)?.label ||
             airportOpsSubItems.find(s => s.id === activeTab)?.label ||
             (activeTab === 'platformAdmin' ? 'Platform Admin' :
              activeTab === 'sysadmin' ? 'Infrastructure' : 'Dashboard')}
          </h2>
          <div className="flex items-center gap-4">
            <button className="relative text-gray-400 hover:text-white">
              <Bell size={20} />
              {notifications > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                  {notifications}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default App;